import React, { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Header from './components/Header'
import Footer from './components/Footer'
import { getEvent, getFestival, getTheatre, isProgramOver, posterUrl } from '../data/loadData'
import './Oyun.css'

const filled = (value) => String(value || '').trim()

const looksLikeHtml = (value) => /<[a-z][\s\S]*>/i.test(value || '')

const mapsEmbed = (value) => {
  const url = filled(value)
  return url.startsWith('https://www.google.com/maps/embed') ? url : ''
}

const splitCopy = (value) => {
  const parts = String(value || '')
    .split('*')
    .map((part) => part.trim())
    .filter(Boolean)

  const paragraphs = []
  const notes = []

  parts.forEach((part) => {
    const labeled = /^[^:]{2,40}:/.test(part) && part.length < 180
    if (labeled) notes.push(part)
    else paragraphs.push(part)
  })

  return { paragraphs, notes }
}

export default function Oyun() {
  const [params] = useSearchParams()
  const year = params.get('f')
  const id = params.get('id')
  const found = getEvent(year, id)
  const festival = getFestival(year)
  const show = found?.event
  const program = found?.program

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [year, id])

  const place = [filled(show?.sehir), filled(show?.ulke)].filter(Boolean).join(', ')
  const company = [filled(show?.topluluk), place].filter(Boolean).join(' · ')

  const facts = show ? [
    { label: 'Tarih', value: filled(show.tarih) },
    { label: 'Saat', value: filled(show.saat) },
    { label: 'Salon', value: filled(show.salon) },
    { label: 'Süre', value: filled(show.sure) },
    { label: 'Bilet', value: filled(show.fiyat) },
    { label: 'Yaş', value: filled(show.yas) },
  ].filter((item) => item.value) : []

  const credits = show ? [
    { label: 'Yazan', value: filled(show.yazar) },
    { label: 'Yönetmen', value: filled(show.yonetmen) },
    { label: 'Uyarlayan', value: filled(show.uyarlayan) },
    { label: 'Konuşmacı', value: filled(show.konusmaci) },
    { label: 'Müzisyen', value: filled(show.muzisyen) },
    { label: 'Perde', value: filled(show.perde) },
  ].filter((item) => item.value) : []

  const copy = splitCopy(show?.aciklama)
  const poster = show?.afis ? posterUrl(festival, show.afis) : ''
  const theatre = show ? getTheatre(show.salon) : null
  const venue = theatre ? {
    name: filled(show.salon) || filled(theatre.name),
    address: filled(theatre.address),
    map: mapsEmbed(theatre.location),
  } : null
  const showVenue = venue && (venue.name || venue.address || venue.map)

  const ticket = () => {
    if (!show) return null
    if (!filled(show.aciklama) && program?.emptyDescriptionNote) {
      return <p className="playbill-note">{program.emptyDescriptionNote}</p>
    }
    if (isProgramOver(year) || !filled(show.bilet)) return null
    if (show.bilet === 'free') {
      return (
        <p className="playbill-note">
          Biletler 10 Ekim itibariyle {show.salon}'inden ücretsiz olarak temin edilebilir.
        </p>
      )
    }
    if (show.bilet === '-') {
      return <p className="playbill-note">Etkinlik ücretsizdir.</p>
    }
    return (
      <a className="btn" href={show.bilet} target="_blank" rel="noreferrer">
        Bilet al
      </a>
    )
  }

  return (
    <React.Fragment>
      <Header />
      <section className="playbill-stage">
        <div className="container">
          <div className="playbill-stage-row">
            <Link className="playbill-back" to="/">Programa dön</Link>
            {festival?.name ? <p className="playbill-fest">{festival.name}</p> : null}
          </div>
        </div>
      </section>

      <section className="playbill">
        <div className="container">
          {show ? (
            <div className="playbill-grid">
              <div className={poster ? 'playbill-poster' : 'playbill-poster playbill-poster--empty'}>
                {poster ? (
                  <img src={poster} alt={filled(show.oyun) || 'Afiş'} />
                ) : (
                  <span>{filled(show.tur) || 'Gösteri'}</span>
                )}
              </div>

              <div>
                <p className="playbill-kicker">{filled(show.tur) || 'Gösteri'}</p>
                <h1 className="playbill-title">{show.oyun}</h1>
                {company ? <p className="playbill-company">{company}</p> : null}

                {facts.length ? (
                  <div className="playbill-facts">
                    {facts.map((item) => (
                      <div className="playbill-fact" key={item.label}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    ))}
                  </div>
                ) : null}

                {credits.length ? (
                  <dl className="playbill-credits">
                    {credits.map((item) => (
                      <div key={item.label}>
                        <dt>{item.label}</dt>
                        <dd>{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}

                {filled(show.aciklama) ? (
                  <div className="playbill-copy">
                    {looksLikeHtml(show.aciklama) ? (
                      <div dangerouslySetInnerHTML={{ __html: show.aciklama }} />
                    ) : (
                      copy.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
                    )}
                    {copy.notes.length ? (
                      <ul className="playbill-notes">
                        {copy.notes.map((note) => <li key={note}>{note}</li>)}
                      </ul>
                    ) : null}
                  </div>
                ) : null}

                {filled(show.video) ? (
                  <a className="playbill-video" href={show.video} target="_blank" rel="noreferrer">
                    Tanıtım videosu
                  </a>
                ) : null}

                <div className="playbill-ticket">{ticket()}</div>
              </div>
            </div>
          ) : (
            <div className="playbill-missing">
              <h1>Bu etkinlik bulunamadı</h1>
              <p>Programda böyle bir kayıt yok. Ana sayfadaki festival programına dönebilirsiniz.</p>
            </div>
          )}
          {show && showVenue ? (
            <section className={venue.map ? 'playbill-venue' : 'playbill-venue playbill-venue--text'} aria-label="Sahne">
              <div>
                <p className="playbill-kicker">Sahne</p>
                {venue.name ? <h2 className="playbill-venue-name">{venue.name}</h2> : null}
                {venue.address ? <p className="playbill-venue-address">{venue.address}</p> : null}
              </div>
              {venue.map ? (
                <div className="playbill-map">
                  <iframe
                    title={`${venue.name || 'Sahne'} konumu`}
                    src={venue.map}
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      </section>
      <Footer />
    </React.Fragment>
  )
}
